import { useState } from 'react';

import modalStyles from './alarmModal.module.css';
import { DataTableColumn } from '@/components/DataTable';
import { Button } from '@/components/Form/Button';
import { ALARM_TYPES, orderAlarmsData } from '@/constants/turbines';
import { AlarmData } from '@/slices/types';
import AlertAcknowledgedIcon from '@/solaris-lens-common-lib/src/assets/alertAcknowledged.svg?react';
import EyeIcon from '@/solaris-lens-common-lib/src/assets/eyeOpen.svg?react';
import { msToDuration } from '@/solaris-lens-common-lib/src/utils/dates';

export interface InstalledProductResponse {
  data: {
    info?: {
      alarms?: AlarmData[];
      shutdowns?: AlarmData[];
    };
  };
  info?: {
    alarms?: AlarmData[];
    shutdowns?: AlarmData[];
  };
  meta?: any;
}

export const alarmsDataOrder = {
  [ALARM_TYPES.ALERT]: 0,
  [ALARM_TYPES.WARNING]: 1
};

export const orderData = orderAlarmsData;

export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;

    return `${month}/${day}/${year} ${displayHours}:${minutes} ${ampm}`;
  } catch {
    return dateString;
  }
};

export const getAlarmsColumns = (
  onDetailsClick: (alarm: AlarmData) => void,
  onAcknowledge?: (tag: string) => void
): DataTableColumn<AlarmData>[] => [
  {
    field: 'name',
    headerName: 'ALARM',
    width: '34%',
    sortable: true
  },
  {
    field: 'startDate',
    headerName: 'ALARM DATE',
    width: '13%',
    sortable: true,
    renderCell: ({ value }) => formatDate(value || '')
  },
  {
    field: 'duration',
    headerName: 'DURATION',
    width: '9%',
    sortable: true,
    renderCell: ({ value }) => {
      if (!value) return '-';
      if (typeof value === 'string') {
        return value;
      }
      if (value > 1000) {
        return msToDuration(value);
      }
      const hours = Math.floor(value / 3600);
      const minutes = Math.floor((value % 3600) / 60);
      const seconds = value % 60;

      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    }
  },
  {
    field: 'ackBy',
    headerName: 'ACK BY',
    width: '12%',
    sortable: true,
    renderCell: ({ value }) => value || '-'
  },
  {
    field: 'ackAt',
    headerName: 'ACK DATE',
    width: '13%',
    sortable: true,
    renderCell: ({ value }) => (value ? formatDate(value) : '-')
  },
  {
    field: 'description',
    headerName: 'ACTIONS',
    width: '19%',
    sortable: false,
    renderCell: ({ row }) => (
      <div
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          justifyContent: 'flex-end'
        }}
        onClick={e => e.stopPropagation()}
      >
        <Button
          label="Details"
          icon={EyeIcon}
          onClick={() => onDetailsClick(row)}
          color="blue"
          solid={false}
          size="small"
        />
        <Button
          label="Acknowledge"
          icon={AlertAcknowledgedIcon}
          onClick={() => {
            if (onAcknowledge && row.tag) {
              onAcknowledge(row.tag);
            }
          }}
          color="blue"
          solid={false}
          size="small"
          disabled={row.acknowledged || !row.tag || !onAcknowledge}
        />
      </div>
    )
  }
];

export const AlarmModalContent = ({
  alarm,
  onClose
}: {
  alarm: AlarmData;
  onClose: () => void;
}) => {
  const [isSubscribed, setIsSubscribed] = useState(alarm.isSubscribed || false);

  return (
    <div className={modalStyles.modalContentWrapper}>
      <div className={modalStyles.modalDescription}>
        {alarm.description || ''}
      </div>
      <div className={modalStyles.modalFooter}>
        <div className={modalStyles.switchContainer}>
          <label className={modalStyles.toggleSwitch}>
            <input
              type="checkbox"
              checked={isSubscribed}
              onChange={e => setIsSubscribed(e.target.checked)}
            />
            <span className={modalStyles.toggleSlider}></span>
          </label>
          <span className={modalStyles.switchLabel}>
            Subscribe to this alarm
          </span>
        </div>
        <Button
          label="Close"
          onClick={onClose}
          color="blue"
          solid={false}
          size="small"
        />
      </div>
    </div>
  );
};
